// Số liệu cho trang Tổng quan tài liệu: kỳ báo cáo, cờ "cần xử lý", tỷ lệ đọc, hiệu lực tại thời điểm, thời gian duyệt
import dayjs, { type Dayjs } from 'dayjs';
import type { Doc, Employee } from './data';
import { docState, recipientIds } from './logic';

export const OVERDUE_DAYS = 3;
export const EXPIRING_DAYS = 30;
export const LOWREAD_DAYS = 7;
export const LOWREAD_RATE = 0.5;
export const SLA_DAYS = 2;
export const STORAGE_QUOTA = 10 * 1024 ** 3;

export type PeriodKey = '7d' | '30d' | 'quarter' | 'year' | '12m';
export const PERIOD_LABEL: Record<PeriodKey, string> = { '7d': '7 ngày', '30d': '30 ngày', quarter: 'Quý này', year: 'Năm nay', '12m': '12 tháng' };

export interface Period { start: Dayjs; end: Dayjs; prevStart: Dayjs; prevEnd: Dayjs }

/** Kỳ hiện tại + kỳ trước cùng độ dài (để so sánh) */
export function periodRange(key: PeriodKey, now = dayjs()): Period {
  let start: Dayjs;
  if (key === '7d') start = now.subtract(7, 'day');
  else if (key === '30d') start = now.subtract(30, 'day');
  else if (key === 'quarter') start = now.month(Math.floor(now.month() / 3) * 3).startOf('month');
  else if (key === 'year') start = now.startOf('year');
  else start = now.subtract(12, 'month');
  const len = now.diff(start);
  return { start, end: now, prevStart: start.subtract(len, 'ms'), prevEnd: start };
}

export const inRange = (iso: string | undefined, from: Dayjs, to: Dayjs) => !!iso && iso >= from.toISOString() && iso < to.toISOString();

export type Flag = 'overdue' | 'expiring' | 'lowread' | 'comments';
export const FLAG_LABEL: Record<Flag, string> = {
  overdue: `Chờ duyệt quá ${OVERDUE_DAYS} ngày`,
  expiring: `Sắp hết hiệu lực trong ${EXPIRING_DAYS} ngày`,
  lowread: `Ban hành hơn ${LOWREAD_DAYS} ngày, dưới ${LOWREAD_RATE * 100}% người đọc`,
  comments: 'Dự thảo có góp ý mới chưa phản hồi',
};

/** Số người được chia sẻ và số người đã đọc (tính đến thời điểm `at` nếu có) */
export function readStats(d: Doc, people: Employee[], at?: Dayjs) {
  const ids = recipientIds(d, people);
  const limit = at?.toISOString();
  const read = ids.filter(id => d.views[id] && (!limit || d.views[id] <= limit)).length;
  return { total: ids.length, read };
}

/** Các cờ cảnh báo của 1 tài liệu */
export function docFlags(d: Doc, people: Employee[], now = dayjs()): Flag[] {
  const flags: Flag[] = [];
  if (d.deletedAt) return flags;
  if (d.status === 'pending' && d.submittedAt && now.diff(dayjs(d.submittedAt), 'day', true) > OVERDUE_DAYS) flags.push('overdue');
  const state = docState(d, now.format('YYYY-MM-DD'));
  if (state === 'active' && d.effectiveTo && d.effectiveTo <= now.add(EXPIRING_DAYS, 'day').format('YYYY-MM-DD')) flags.push('expiring');
  if (d.status === 'published' && !d.replacedById && d.approvedAt && now.diff(dayjs(d.approvedAt), 'day', true) > LOWREAD_DAYS) {
    const r = readStats(d, people);
    if (r.total > 0 && r.read / r.total < LOWREAD_RATE) flags.push('lowread');
  }
  if (d.status === 'draft' && d.comments.length) {
    const last = d.comments[d.comments.length - 1];
    if (last.userId !== d.createdBy && last.at > d.updatedAt) flags.push('comments');
  }
  return flags;
}

/** Tài liệu có đang hiệu lực tại thời điểm `at` không (dùng so sánh với kỳ trước) */
export function isActiveAt(d: Doc, at: Dayjs, byId: Map<string, Doc>): boolean {
  const iso = at.toISOString();
  const day = at.format('YYYY-MM-DD');
  if (!d.approvedAt || d.approvedAt > iso) return false;
  if (d.effectiveFrom > day || (d.effectiveTo && d.effectiveTo < day)) return false;
  if (d.revokedAt && d.revokedAt <= iso) return false;
  const replacer = d.replacedById ? byId.get(d.replacedById) : undefined;
  if (replacer?.approvedAt && replacer.approvedAt <= iso) return false;
  return true;
}

/** Tỷ lệ đọc chung của các tài liệu đã ban hành tính đến thời điểm `at` */
export function readRateAt(docs: Doc[], people: Employee[], at: Dayjs) {
  let total = 0;
  let read = 0;
  docs.forEach(d => {
    if (!d.approvedAt || d.approvedAt > at.toISOString() || (d.status !== 'published' && d.status !== 'revoked')) return;
    const r = readStats(d, people, at);
    total += r.total;
    read += r.read;
  });
  return total ? read / total : 0;
}

/** Số ngày từ gửi duyệt đến ban hành */
export const approvalDays = (d: Doc) =>
  d.submittedAt && d.approvedAt ? Math.max(0, dayjs(d.approvedAt).diff(dayjs(d.submittedAt), 'hour') / 24) : null;

export const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);

/** Thời gian tương đối: "5 phút", "3 giờ", "hôm qua", "12/09/2026" */
export function relTime(iso: string, now = dayjs()) {
  const mins = now.diff(dayjs(iso), 'minute');
  if (mins < 1) return 'vừa xong';
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = now.startOf('day').diff(dayjs(iso).startOf('day'), 'day');
  if (days === 1) return 'hôm qua';
  if (days < 7) return `${days} ngày trước`;
  return dayjs(iso).format('DD/MM/YYYY');
}
